import { useTranslation } from 'react-i18next';
import { Skeleton, SkeletonText } from '@/components/ui';
import { AppCoverageRail } from '../roleCoverage/AppCoverageRail';
import { CoverageMatrixCard } from '../roleCoverage/CoverageMatrixCard';
import { RoleEditorCard } from '../roleCoverage/RoleEditorCard';
import type { RoleCoverageVM } from '../roleCoverage/useRoleCoverage';

/**
 * EAA-1841: the **board** view of "Liên kết vai trò" — the rail + coverage
 * matrix + per-app chip editor that used to be its own `/console/role-coverage`
 * route (design frame 42).
 *
 * Lifted out of the deleted `RoleCoveragePage` unchanged apart from losing the
 * page chrome (`<main>`, H1, subtitle), which the merged `RoleMapPage` now owns
 * once for both views.
 *
 * This view is ECOSYSTEM-DEFAULT ONLY and that is a backend fact, not a choice:
 * `fetchRoleMapCoverage` filters `organization__isnull=True` and takes no
 * organization param at all. The list view's "Phạm vi" control therefore has
 * nothing to switch here — see `RoleMapPage` for the notice that says so rather
 * than showing a scope control that would silently do nothing.
 */
export function RoleMapBoardView({ vm }: { vm: RoleCoverageVM }) {
    const { t } = useTranslation();
    if (vm.isLoading) {
        return (
            <div className="rc-columns" role="status" aria-live="polite">
                <span className="portal-sr-only">
                    {t('console.roleMap.boardView.srLoadingText')}
                </span>
                <div className="rc-rail">
                    <SkeletonText lines={2} />
                    <Skeleton height={34} />
                    <Skeleton height={220} />
                </div>
                <div className="rc-right-col">
                    <div className="hz-card">
                        <SkeletonText lines={1} />
                        <Skeleton height={160} />
                    </div>
                    <div className="hz-card">
                        <SkeletonText lines={2} />
                        <Skeleton height={160} />
                    </div>
                </div>
            </div>
        );
    }

    if (vm.isError) {
        return (
            <p className="portal-admin-error">
                {t('console.roleMap.boardView.errorText')}{' '}
                <button type="button" className="portal-btn" onClick={vm.retry}>
                    {t('console.roleMap.boardView.retry')}
                </button>
            </p>
        );
    }

    return (
        <div className="rc-columns">
            <AppCoverageRail vm={vm.board} />
            <div className="rc-right-col">
                <CoverageMatrixCard vm={vm.board} />
                <RoleEditorCard vm={vm} />
            </div>
        </div>
    );
}

export default RoleMapBoardView;
